import { HardwareService } from '../../HardwareService';
import { ConnectionType } from '../../shared/types';
import { Scanner } from '../Scanner';
import type { BarcodeData } from '../types';
import { resolvePdf417DecoderWithin } from './decoders/resolve';

/** marginY is 0.27, not 0.3: a 432px band clips a 455px-tall symbol, and a clipped symbol is a hard no-read. */
const RoiMargin = { x: 0.05, y: 0.27 };

/** The crop as CSS insets. Frozen and exported because two apps draw a guide claiming to be this crop. */
export const ScanGuideInsets = Object.freeze({
  left: `${RoiMargin.x * 100}%`,
  right: `${RoiMargin.x * 100}%`,
  top: `${RoiMargin.y * 100}%`,
  bottom: `${RoiMargin.y * 100}%`,
});

/** Per ML Kit, a full-width AAMVA symbol wants this many pixels across; a 720p ROI is 1152, under it. */
const MinReliableBarcodeWidthPx = 1156;

const roiWidthPx = (streamWidthPx: number): number => Math.floor(streamWidthPx * (1 - RoiMargin.x * 2));

/** The region `scan` really crops, so a caller can draw or assert it without reimplementing `grabRoi`. */
export const scanRoiSizePx = (streamWidthPx: number, streamHeightPx: number): { width: number; height: number } => ({
  width: roiWidthPx(streamWidthPx),
  height: Math.floor(streamHeightPx * (1 - RoiMargin.y * 2)),
});

/** False when even a perfectly framed barcode cannot reach the reliable width on this stream. */
export const isStreamWidthReliable = (streamWidthPx: number): boolean =>
  roiWidthPx(streamWidthPx) >= MinReliableBarcodeWidthPx;

/** A budget, not a delay: with `tryHarder` and `tryRotate` a decode is usually the longer of the two. */
const FrameBudgetMs = 125;

/** Progress is throttled to this. A phase change and the terminal report ignore it. */
const ProgressIntervalMs = 500;

export type CameraScanTelemetry = {
  decoder: string | null;
  frames: number;
  elapsedMs: number;
  roiWidthPx: number;
  roiHeightPx: number;
  streamWidthPx: number;
  streamHeightPx: number;
};

export type CameraScanOutcome = {
  barcode: BarcodeData | null;
  telemetry: CameraScanTelemetry;
};

/** Drives the on-screen guide's colour, so every member is a fixed token. */
export type CameraScanPhase =
  | 'starting'
  | 'no-decoder'
  | 'no-stream'
  | 'waiting-for-frames'
  | 'decoding'
  | 'stream-ended'
  | 'decoded'
  | 'timed-out'
  | 'aborted';

/** Numbers and fixed tokens only. Emitted on a throttle, never once per frame. */
export type CameraScanProgress = {
  phase: CameraScanPhase;
  decoder: string | null;
  frames: number;
  elapsedMs: number;
  roiWidthPx: number;
  roiHeightPx: number;
  streamWidthPx: number;
  streamHeightPx: number;
};

export type CameraScanOptions = {
  /** Gives up and lets the caller offer manual entry. */
  timeoutMs?: number;
  signal?: AbortSignal;
  /** Fires on a throttle; callers are expected to collapse it to a phase change, not re-render per call. */
  onProgress?: (progress: CameraScanProgress) => void;
};

/**
 * Decodes frames off a preview the caller opened and owns. Acquisition lives in
 * `requestRearCameraStream`, so this class never opens or stops a stream.
 */
export class CameraScanner extends Scanner {
  private stream: MediaStream | null = null;
  private canvas: HTMLCanvasElement | null = null;

  constructor() {
    super(ConnectionType.camera);
  }

  get id(): string {
    return 'camera-scanner';
  }
  get isConnected(): boolean {
    return this.stream != null;
  }
  override get name(): string {
    return 'Camera Scanner';
  }

  /** Nothing to connect: the caller supplies the stream on the video element it passes to `scan`. */
  async doConnect(): Promise<boolean> {
    return this.stream != null;
  }

  /** Drops references only. The caller stops the tracks, because the caller opened them. */
  async doDisconnect(): Promise<boolean> {
    this.stream = null;
    this.canvas = null;
    return true;
  }

  private adopt(video: HTMLVideoElement): boolean {
    const existing = video.srcObject;
    if (!(existing instanceof MediaStream) || existing.getVideoTracks().length === 0) {
      return false;
    }
    this.stream = existing;
    return true;
  }

  /**
   * Continuous, not single-shot: per-frame decode rates are well under 100%. Emits the barcode
   * event too, so `useScanner()` consumers see a camera decode as they see a hardware scan.
   */
  async scan(video: HTMLVideoElement, options: CameraScanOptions = {}): Promise<CameraScanOutcome> {
    const startedAt = Date.now();
    const live = this.progressReporter(startedAt, options.onProgress);
    live.report('starting');
    const timeoutMs = options.timeoutMs ?? 30_000;

    // The budget and the signal must cover resolving the decoder too: the first resolution fetches
    // the ~931 KB wasm, and an un-raced wait hung `scan()` for the whole session.
    const budget = Math.max(0, timeoutMs - (Date.now() - startedAt));
    const resolution = await resolvePdf417DecoderWithin(budget, options.signal);
    if (resolution.gaveUp) {
      // Not 'no-decoder': that would tell the operator this device cannot scan, when the decoder
      // had merely not arrived yet.
      live.report(options.signal?.aborted ? 'aborted' : 'timed-out', true);
      HardwareService.logger.d('Camera scan gave up while resolving the decoder', {
        aborted: options.signal?.aborted === true,
        timeoutMs,
      });
      return { barcode: null, telemetry: this.telemetry(null, 0, startedAt, 0, 0) };
    }

    const decoder = resolution.decoder;
    live.state.decoder = decoder?.name ?? null;

    // Checked before `adopt`: evaluating `adopt(video)` first still set `this.stream` when there was
    // no decoder, and the early return skipped `doDisconnect`, so `isConnected` read true after a
    // scan that never looked at a frame.
    if (!decoder) {
      // Logged at error level: without a decoder the loop never looks at a frame, and a debug line
      // would vanish in production exactly when it is needed.
      HardwareService.logger.e('Camera scan cannot start', { reason: 'no PDF417 decoder' });
      return this.giveUp('no-decoder', live, startedAt);
    }
    if (!this.adopt(video)) {
      HardwareService.logger.e('Camera scan cannot start', { reason: 'no video stream on the element' });
      return this.giveUp('no-stream', live, startedAt);
    }

    while (Date.now() - startedAt < timeoutMs && !options.signal?.aborted) {
      // Terminal, not a label: a dead track leaves the last frame's dimensions on the video element,
      // so continuing would re-decode the same stale frame for the rest of the budget.
      if (this.isStreamEnded()) {
        return this.giveUp('stream-ended', live, startedAt);
      }

      const attemptedAt = Date.now();
      const frame = this.grabRoi(video);
      if (frame) {
        live.state.roiWidthPx = frame.width;
        live.state.roiHeightPx = frame.height;
        live.state.frames += 1;
        live.report('decoding');
        const barcode = await decoder.decode(frame);
        // Re-checked after the decode: a wasm decode runs for hundreds of ms, and accepting a result
        // past any of these dispatches a stale or cancelled barcode on the process-wide scanner events.
        if (options.signal?.aborted) {
          break;
        }
        if (this.isStreamEnded()) {
          return this.giveUp('stream-ended', live, startedAt);
        }
        if (Date.now() - startedAt >= timeoutMs) {
          return this.giveUp('timed-out', live, startedAt);
        }
        if (barcode) {
          live.report('decoded', true);
          const telemetry = this.telemetry(
            decoder.name,
            live.state.frames,
            startedAt,
            live.state.roiWidthPx,
            live.state.roiHeightPx,
          );
          HardwareService.logger.d('Camera scanner decoded a PDF417', telemetry);
          await this.doDisconnect();
          this.emitBarcodeEvent(barcode);
          return { barcode, telemetry };
        }
      } else {
        live.report('waiting-for-frames');
      }
      // Yield for whatever is left of the budget, and at least a tick so the UI can paint.
      await new Promise((resolve) => setTimeout(resolve, Math.max(1, FrameBudgetMs - (Date.now() - attemptedAt))));
    }

    return this.giveUp(options.signal?.aborted ? 'aborted' : 'timed-out', live, startedAt);
  }

  /**
   * The one way out of `scan` without a decode: no decoder, no stream, an ended track, a late
   * decode, a timeout or a cancel. Reports the terminal phase, then drops this scanner's references
   * so `isConnected` cannot stay true after the scan has ended. Tracks are never stopped here — the
   * caller opened them and the caller owns them.
   */
  private async giveUp(
    phase: CameraScanPhase,
    live: ReturnType<CameraScanner['progressReporter']>,
    startedAt: number,
  ): Promise<CameraScanOutcome> {
    live.report(phase, true);
    const telemetry = this.telemetry(
      live.state.decoder,
      live.state.frames,
      startedAt,
      live.state.roiWidthPx,
      live.state.roiHeightPx,
    );
    HardwareService.logger.d('Camera scanner gave up without a decode', { phase, ...telemetry });
    await this.doDisconnect();
    return { barcode: null, telemetry };
  }

  /** A live track that has ended still leaves the last frame's dimensions on the video element. */
  private isStreamEnded(): boolean {
    const track = this.stream?.getVideoTracks()[0];
    return track != null && track.readyState === 'ended';
  }

  /** Throttled so a 30s scan reports ~60 times, not ~240, and never inside the decode itself. */
  private progressReporter(startedAt: number, onProgress?: (progress: CameraScanProgress) => void) {
    const state = { decoder: null as string | null, frames: 0, roiWidthPx: 0, roiHeightPx: 0 };
    let lastPhase: CameraScanPhase | null = null;
    let lastReportAt = 0;

    const report = (phase: CameraScanPhase, force = false): void => {
      if (!onProgress) {
        return;
      }
      const now = Date.now();
      if (!force && phase === lastPhase && now - lastReportAt < ProgressIntervalMs) {
        return;
      }
      lastPhase = phase;
      lastReportAt = now;
      const track = this.stream?.getVideoTracks()[0]?.getSettings();
      onProgress({
        phase,
        decoder: state.decoder,
        frames: state.frames,
        elapsedMs: now - startedAt,
        roiWidthPx: state.roiWidthPx,
        roiHeightPx: state.roiHeightPx,
        streamWidthPx: track?.width ?? 0,
        streamHeightPx: track?.height ?? 0,
      });
    };

    return { state, report };
  }

  /** Null until the video element has real dimensions, which is a frame or two after play(). */
  private grabRoi(video: HTMLVideoElement): ImageData | null {
    const { videoWidth, videoHeight } = video;
    if (!videoWidth || !videoHeight) {
      return null;
    }

    // Through the exported helper, so the crop taken here is the one the guide is drawn from.
    const { width, height } = scanRoiSizePx(videoWidth, videoHeight);
    const canvas = (this.canvas ??= document.createElement('canvas'));
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) {
      return null;
    }
    const sourceX = Math.floor(videoWidth * RoiMargin.x);
    const sourceY = Math.floor(videoHeight * RoiMargin.y);
    context.drawImage(video, sourceX, sourceY, width, height, 0, 0, width, height);
    return context.getImageData(0, 0, width, height);
  }

  private telemetry(
    decoder: string | null,
    frames: number,
    startedAt: number,
    roiWidthPx: number,
    roiHeightPx: number,
  ): CameraScanTelemetry {
    const track = this.stream?.getVideoTracks()[0]?.getSettings();
    return {
      decoder,
      frames,
      elapsedMs: Date.now() - startedAt,
      roiWidthPx,
      roiHeightPx,
      streamWidthPx: track?.width ?? 0,
      streamHeightPx: track?.height ?? 0,
    };
  }
}
